//@ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import Svg from "../../Svg/Svg";
import OccupiedField from "../OccupiedField/OccupiedField";
import {
	FieldWrapper,
	FieldActionWrapper,
	Modal,
	DialogContainer,
	ModalTitleContainer,
	ModalTitle,
	ModelSubHeading,
	ModalFooter,
	Button,
} from "@factorearth/component-library";
import styles from "./GeoSpatialField.module.scss";
import { FiMapPin, FiX } from "react-icons/fi";

import FieldAction from "../../FieldAction/FieldAction";
import { UpdateContext } from "utils/UpdateContext";

import { GeoLocationService } from "services/location/geoLocationService";
import updateSingleFieldInstance from "utils/_updateSingleFieldInstance";
import { createFieldForBatchResolver } from "db/queries/fieldInstanceQueries";
import { createRevisionForBatchResolver, putRevisions } from "db/queries/revisionQueries";
import { EmbeddedTextInput } from "./EmbeddedTextField/EmbeddedTextField";

/**
 * --- Geo value contract (v1) ---
 * fieldInstance.value:
 *   "" (unset) OR JSON string: {"lat": number, "lon": number}
 * Accuracy not stored (yet). If later needed, add "acc".
 */

type GeoValue = { lat: number; lon: number };

function safeParseGeo(raw?: string | null): GeoValue | null {
	if (!raw || !raw.trim()) return null;
	try {
		const obj = JSON.parse(raw);
		if (!obj || typeof obj !== "object") return null;
		const lat = Number(obj.lat);
		const lon = Number(obj.lon);
		if (Number.isFinite(lat) && Number.isFinite(lon)) return { lat, lon };
		return null;
	} catch {
		return null;
	}
}

function stringifyGeo(lat: number, lon: number): string {
	// Keep key names stable for export: lat/lon
	return JSON.stringify({ lat, lon });
}

function normalizeCoordInput(s: string): string {
	// allow digits, -, .
	return s.replace(/[^\d\.\-]/g, "");
}

function toFiniteNumberOrNaN(s: string): number {
	const n = Number(s);
	return Number.isFinite(n) ? n : NaN;
}

function isValidLatLon(lat: number, lon: number) {
	return Number.isFinite(lat) && Number.isFinite(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
}

// Optional formatting for display. Keep raw editing separate from display.
function formatCoord(n: number) {
	// Up to 6 decimals is common for GPS (~0.11m at equator). Adjust if desired.
	return Number.isFinite(n) ? String(Number(n.toFixed(6))) : "";
}

export default function GeoSpatialField({
	fieldInstance,
	fieldTemplate,
	color,
	online,
	userDoc,
	setupDrawer,
	fromTable,
	username,
	authUsername,
	moreHorizon,
}: {
	fieldInstance: any;
	fieldTemplate: any;
	color: any;
	online: boolean;
	userDoc: any;
	setupDrawer: any;
	fromTable: boolean;
	username: string;
	authUsername: string;
	moreHorizon: JSX.Element;
}) {
	// Parse the stored value once per value change
	const parsed = useMemo(() => safeParseGeo(fieldInstance.value), [fieldInstance.value]);

	// Local input state so user can type partial data without breaking JSON
	const [latText, setLatText] = useState(parsed ? formatCoord(parsed.lat) : "");
	const [lonText, setLonText] = useState(parsed ? formatCoord(parsed.lon) : "");

	const [message, setMessage] = useState<string | null>(null);
	const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
	const [geoResults, setGeoResults] = useState<{ latitude: number; longitude: number; accuracy: number }>({
		latitude: 0,
		longitude: 0,
		accuracy: 0,
	});

	// Keep local state in sync if fieldInstance updates from outside (collab/offline sync)
	useEffect(() => {
		const p = safeParseGeo(fieldInstance.value);
		setLatText(p ? formatCoord(p.lat) : "");
		setLonText(p ? formatCoord(p.lon) : "");
	}, [fieldInstance.value]);

	async function commitIfValid(latStr: string, lonStr: string) {
		const lat = toFiniteNumberOrNaN(latStr);
		const lon = toFiniteNumberOrNaN(lonStr);

		// If either is missing/invalid, do not write anything (lazy init)
		if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

		if (!isValidLatLon(lat, lon)) {
			setMessage("Latitude must be -90..90 and Longitude must be -180..180.");
			return;
		}

		setMessage(null);

		const newValue = stringifyGeo(lat, lon);

		// If unchanged, do nothing
		if ((fieldInstance.value ?? "") === newValue) return;

		const now = new Date().toISOString();

		// Build for resolver/update
		const updated = createFieldForBatchResolver({
			...fieldInstance,
			value: newValue,
			lastAuthor: userDoc.username,
			editing: "",
			updatedAt: now,
		});

		// Single revision for the composite field
		const revision = createRevisionForBatchResolver({
			fieldInstance: updated.id,
			value: updated.value,
			group: updated.group,
			formGroup: updated.formGroup,
			author: userDoc.username,
		});

		putRevisions([revision], online);
		await updateSingleFieldInstance(updated);
	}

	async function handleMapPinClick() {
		setMessage(null);

		const result = await GeoLocationService.getCurrentPosition();

		// For non location allowed laptop testing -
		// const result = {
		// 	status: "success",
		// 	latitude: 37.7749,
		// 	longitude: -122.4194,
		// 	accuracy: 5
		// }

		if (result.status === "success") {
			const { latitude, longitude, accuracy } = result;
			setGeoResults({ latitude, longitude, accuracy });
			setConfirmationModalOpen(true);
		} else {
			console.log("Error getting location data:", result);
			setMessage(result.errorMessage || "Unable to get location.");
		}
	}

	async function confirmUseCurrentLocation() {
		setConfirmationModalOpen(false);

		const lat = geoResults.latitude;
		const lon = geoResults.longitude;

		// Update UI immediately
		setLatText(formatCoord(lat));
		setLonText(formatCoord(lon));

		// Commit single value
		await commitIfValid(String(lat), String(lon));
	}

	function commitIfValid(latStr: string, lonStr: string) {
		// for text typing in of the stuff.
	}
	return (
		<FieldWrapper
			colorPalette={color}
			label={!fromTable ? fieldTemplate.header : undefined}
			link={fieldInstance.altInstanceId ? <Svg icon="LINKED" /> : undefined}
			data-test={"Field-Header"}>
			{username && username !== authUsername ? (
				<OccupiedField username={username} moreHorizon={moreHorizon} />
			) : (
				<FieldActionWrapper>
					<div className={styles.coordFieldRow}>
						<div
							className={`${styles.coordInputShell} ${moreHorizon ? styles.withMoreHorizon : ""}`}
							data-fieldtemplateid={fieldTemplate.id}
							style={{
								color: color.text.primary,
								backgroundColor: color.background.primary,
								borderColor: color.border.primary,
							}}
							role="group"
							aria-label={`Coordinates - ${fieldTemplate.header}`}
							onKeyDownCapture={(e) => {
								// Keep your "/" shortcut
								if (e.key === "/") {
									const root = e.currentTarget as HTMLElement;
									const inputs = root.querySelectorAll("input,textarea,[contenteditable='true']");
									if (document.activeElement === inputs?.[0]) {
										e.preventDefault();
										(inputs?.[1] as HTMLElement | undefined)?.focus?.();
									}
								}
								// Optional: enter commits if both are valid
								if (e.key === "Enter") {
									// do not prevent default globally; only if focused on our inputs
									const el = document.activeElement as HTMLElement | null;
									if (el?.tagName === "INPUT") {
										commitIfValid(latText, lonText);
									}
								}
							}}>
							{/* Single FieldAction for the composite geo field */}
							{/* <UpdateContext.Consumer>
						{({ updateObj, setupChanged }) => (
						<div className={styles.coordPart} data-test={`coord-geo-${fieldInstance.id}`}>
							<FieldAction
							setupDrawer={setupDrawer}
							setupChanged={setupChanged}
							fromTable={true}
							fieldInstanceId={fieldInstance.id}
							updateObj={updateObj?.[fieldInstance.id]}
							fieldInstance={fieldInstance}
							fieldTemplate={fieldTemplate}
							online={online}
							userDoc={userDoc}
							/>
						</div>
						)}
					</UpdateContext.Consumer> */}

							{/* Latitude input */}
							<EmbeddedTextInput
								colorPalette={color}
								value={latText}
								placeholder="lat"
								inputMode="decimal"
								onChange={(e) => setLatText(normalizeCoordInput(e.target.value))}
								onBlur={() => commitIfValid(latText, lonText)}
							/>

							<span className={styles.coordSep} aria-hidden="true">
								/
							</span>

							<EmbeddedTextInput
								colorPalette={color}
								value={lonText}
								placeholder="lon"
								inputMode="decimal"
								onChange={(e) => setLonText(normalizeCoordInput(e.target.value))}
								onBlur={() => commitIfValid(latText, lonText)}
							/>

							<button type="button" className={styles.geoIconButton} onClick={handleMapPinClick} aria-label="Use current location">
								<FiMapPin size={16} />
							</button>

							{moreHorizon && <div className={styles.coordMoreHorizon}>{moreHorizon}</div>}
						</div>

						{message && <div className={styles.message}>{message}</div>}
					</div>
				</FieldActionWrapper>
			)}

			<UpdateGISConfirmationModal
				color={color}
				confirmationModalOpen={confirmationModalOpen}
				setConfirmationModalOpen={setConfirmationModalOpen}
				geoResults={geoResults}
				onConfirm={confirmUseCurrentLocation}
			/>
		</FieldWrapper>
	);
}

function UpdateGISConfirmationModal({
	color,
	confirmationModalOpen,
	setConfirmationModalOpen,
	geoResults,
	onConfirm,
}: {
	color: any;
	confirmationModalOpen: boolean;
	setConfirmationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
	geoResults: { latitude: number; longitude: number; accuracy: number };
	onConfirm: () => Promise<void>;
}) {
	function handleCancel() {
		setConfirmationModalOpen(false);
	}

	return (
		<Modal open={confirmationModalOpen} colorPalette={color} setOpen={setConfirmationModalOpen}>
			<DialogContainer>
				<ModalTitleContainer>
					<ModalTitle>Update coordinates</ModalTitle>
					<FiX onClick={handleCancel} size={24} />
				</ModalTitleContainer>

				<ModelSubHeading>
					This will update the coordinates to {geoResults.latitude}, {geoResults.longitude}
					{geoResults.accuracy ? ` (accuracy ~ ${geoResults.accuracy})` : ""}
				</ModelSubHeading>

				<ModalFooter>
					<Button colorPalette={color} variant="filled" label="Confirm" onClick={onConfirm} />
					<Button colorPalette={color} variant="destructive" label="Cancel" onClick={handleCancel} />
				</ModalFooter>
			</DialogContainer>
		</Modal>
	);
}
